/**
 * Presentation helpers for the Mobile Forensics view — pure functions only.
 *
 * The view renders evidence, so formatting is deliberately conservative:
 * timestamps come out numeric (locale-aware but unambiguous), identities are
 * shown verbatim (E.164 / app handles), and nothing is ever invented for a
 * missing value — a null stays visibly empty.
 */

import type { CommsConversation, CommsMessage } from '@/services/api/comms'

export const DIRECTION_OUT = 1

type Translate = (key: string, opts?: { defaultValue?: string }) => string

/** App display name via i18n (`comms.app.<id>`); an app the locale does not
 * know yet shows its raw id — never hidden, matching how msgType falls back. */
export function appLabel(app: string, t: Translate): string {
  return t(`comms.app.${app}`, { defaultValue: app })
}

/** Known bundle identifiers → the store's app names, so OS-recorded evidence
 * (which speaks bundle ids) and parsed app evidence (which speaks store names)
 * read as one application. Unknown ids stay verbatim — never hidden. */
const BUNDLE_APPS: Record<string, string> = {
  'com.apple.MobileSMS': 'imessage',
  'com.apple.mobilesafari': 'safari',
  'com.google.chrome.ios': 'chrome',
  'net.whatsapp.WhatsApp': 'whatsapp',
  'org.whispersystems.signal': 'signal',
  'com.facebook.Messenger': 'messenger',
  'ph.telegra.Telegraph': 'telegram',
}

/** Display name for an app the OS named by bundle identifier. */
export function bundleAppLabel(bundleId: string, t: Translate): string {
  return appLabel(BUNDLE_APPS[bundleId] ?? bundleId, t)
}

/** Monogram for the conversation avatar — first two significant characters. */
export function conversationInitials(conversation: CommsConversation): string {
  const name = conversationTitle(conversation)
  const letters = name.replace(/[^\p{L}\p{N}]/gu, '')
  if (letters.length >= 2) return letters.slice(0, 2).toUpperCase()
  return name.slice(0, 2).toUpperCase() || '#'
}

/** Thread title: the stored display name, else the other participants. */
export function conversationTitle(conversation: CommsConversation): string {
  if (conversation.displayName?.trim()) return conversation.displayName
  if (conversation.participants.length > 0) return conversation.participants.join(', ')
  return conversation.conversationId
}

export function isGroup(conversation: CommsConversation): boolean {
  return conversation.participants.length > 2
}

export function bubbleTime(iso: string | null, locale: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
}

/** List-row timestamp: time for today, date otherwise. */
export function listTime(iso: string | null, locale: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  return date.toDateString() === new Date().toDateString()
    ? bubbleTime(iso, locale)
    : date.toLocaleDateString(locale, { day: 'numeric', month: 'numeric', year: 'numeric' })
}

export function dayLabel(iso: string, locale: string): string {
  return new Date(iso).toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/** True when `current` starts a new calendar day relative to `previous`. */
export function startsNewDay(previous: CommsMessage | undefined, current: CommsMessage): boolean {
  if (!current.sentAt) return false
  if (!previous?.sentAt) return true
  return new Date(previous.sentAt).toDateString() !== new Date(current.sentAt).toDateString()
}

/**
 * Hours of silence that read as the end of a sitting. Mirrors the pipeline's
 * `gap_hours` (comms_extraction.jsonc) so the thread is grouped the way the
 * windows are — but this is a reading aid only: it never changes what gets
 * promoted, just what is easy to frame.
 */
export const SITTING_GAP_HOURS = 24

/** Silence before `current`, when it is long enough to start a new sitting.
 * Null inside a sitting, and null whenever either side is undated — a missing
 * timestamp is not evidence of silence. */
export function sittingGapHours(
  previous: CommsMessage | undefined,
  current: CommsMessage
): number | null {
  if (!previous?.sentAt || !current.sentAt) return null
  const hours =
    (new Date(current.sentAt).getTime() - new Date(previous.sentAt).getTime()) / 3_600_000
  return hours >= SITTING_GAP_HOURS ? hours : null
}

/** The window's sittings as inclusive index ranges, in order. Every message
 * belongs to exactly one, so the ranges always cover the whole window. */
export function sittings(messages: CommsMessage[]): Array<{ start: number; end: number }> {
  const ranges: Array<{ start: number; end: number }> = []
  messages.forEach((message, index) => {
    const previous = messages[index - 1]
    if (index === 0 || sittingGapHours(previous, message) !== null) {
      ranges.push({ start: index, end: index })
    } else {
      ranges[ranges.length - 1].end = index
    }
  })
  return ranges
}

/** Attachment display name — the path's basename, never the full device path. */
export function attachmentName(ref: string): string {
  const base = ref.split('/').pop() ?? ref
  return base.length > 48 ? `…${base.slice(-45)}` : base
}

/**
 * ISO instant → the value a `datetime-local` input expects.
 *
 * That input speaks **local wall-clock time with no zone**, while the range
 * travels as a UTC instant. Feeding it the ISO string directly would show the
 * UTC clock and silently shift the window by the offset, so the conversion goes
 * through the local getters rather than `toISOString().slice(...)`.
 */
export function toLocalInput(iso: string | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

/** `datetime-local` value → ISO instant; blank or unparseable means "no bound". */
export function toIsoOrUndefined(local: string): string | undefined {
  if (!local) return undefined
  const date = new Date(local)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/** Short label for the active window; an open end reads as an open end. */
export function rangeLabel(range: { from?: string; to?: string }, locale: string): string {
  const day = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'numeric' }) : '…'
  if (!range.from && !range.to) return ''
  return `${day(range.from)} – ${day(range.to)}`
}

/** Whether a time window is in effect — either end set. */
export function isRanged(range: { from?: string; to?: string }): boolean {
  return !!(range.from || range.to)
}
